import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, TextField, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Avatar } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
import AdminApi from "Service/Admin.api";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import { RouteUrls } from "Constants/RouteUrls";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ProfessionalSelectionList: React.FC = () => {
    const [professionals, setProfessionals] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        fetchProfessionals();
    }, []);

    const fetchProfessionals = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Fetching Professionals..." }));
        try {
            const response = await AdminApi.fetchVerifyProfessionalList();
            // Using your earlier logic: handle string response or empty array
            setProfessionals(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(stopLoading());
        }
    };

// --- OPTIMIZED SEARCH LOGIC ---
    const filteredProfs = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return professionals;

        return professionals.filter((p) => {
            const firstName = p.firstName?.toLowerCase() || "";
            const lastName = p.lastName?.toLowerCase() || "";
            const fullName = `${firstName} ${lastName}`;
            const email = p.email?.toLowerCase() || "";

            return (
                firstName.includes(query) ||
                lastName.includes(query) ||
                fullName.includes(query) ||
                email.includes(query)
            );
        });
    }, [searchQuery, professionals]);

    return (
        <Box p={{ xs: 2, sm: 3 }}>
            {/* 3. ADDED BACK BUTTON HERE */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>
            <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
                Select Professional for Payment
            </Typography>

            {/* Responsive Search Field */}
            <TextField
                fullWidth
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 3,
                    maxWidth: { xs: '100%', sm: 400 }
                }}
                InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} />
                }}
                size="small"
            />

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    // Makes the table scrollable horizontally on small screens
                    // and vertically if you set a maxHeight
                    maxHeight: { xs: '60vh', sm: '70vh' },
                    overflow: "auto"
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Professional</StyledTableCell>
                            {/* Hide Email on very small screens to save space, or keep it scrollable */}
                            <StyledTableCell>Email</StyledTableCell>
                            <StyledTableCell align="center">Action</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredProfs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No professionals found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredProfs.map((prof) => (
                                <StyledTableRow key={prof.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                                            <Avatar sx={{ bgcolor: '#283165', width: 32, height: 32, fontSize: '0.9rem' }}>
                                                {prof.firstName[0]}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500}>
                                                {prof.firstName} {prof.lastName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>
                                        {prof.email}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => navigate(`/${RouteUrls.ProfessionalPaymentProcess}/entry/${prof.id}`, { state: { prof } })}
                                            sx={{
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                                borderRadius: 2
                                            }}
                                        >
                                            Process Payments
                                        </Button>
                                    </TableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ProfessionalSelectionList;